import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  Modal,
  PageHeader,
  SelectField,
  TextField,
} from '@safepos/ui';
import { formatRupiah } from '@safepos/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, fileUrl, upload } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';

// ---------- tipe ----------
export interface Category {
  id: string;
  name: string;
  parentId: string | null;
  sort: number;
  active: boolean;
  productCount: number;
  stationId?: string | null;
}
interface VariantDto {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  price: number;
  cost: number;
  active: boolean;
}
export interface ProductDto {
  id: string;
  name: string;
  categoryId: string | null;
  categoryName: string | null;
  description: string | null;
  imageUrl: string | null;
  type: 'simple' | 'variant' | 'bundle' | 'weighed';
  unit: string;
  taxable: boolean;
  trackStock: boolean;
  active: boolean;
  stationId?: string | null;
  variants: VariantDto[];
  modifierGroupIds: string[];
  bundleItems: { variantId: string; qty: number }[];
}
interface ModifierGroup {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  required: boolean;
  productCount: number;
  modifiers: { id: string; name: string; priceDelta: number; active: boolean }[];
}

const TYPE_LABEL = {
  simple: 'Sederhana',
  variant: 'Bervarian',
  bundle: 'Paket',
  weighed: 'Timbang',
} as const;

export interface StationOpt {
  id: string;
  name: string;
  outletName: string;
}
/** Stasiun dapur/bar untuk memilih tujuan tiket (dipetakan per nama di tiap outlet). */
export function StationSelect({
  label = 'Stasiun dapur/bar',
  value,
  onChange,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { data = [] } = useQuery({
    queryKey: ['stations-all'],
    queryFn: () => api.get<StationOpt[]>('/stations'),
  });
  if (data.length === 0) return null;
  return (
    <SelectField label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— Ikut kategori / Dapur —</option>
      {data.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name} ({s.outletName})
        </option>
      ))}
    </SelectField>
  );
}

export function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: string[]) => keys.forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
}

// ---------- Kategori ----------
export function CategoriesPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const { data = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  });
  const [form, setForm] = useState<{
    id?: string;
    name: string;
    parentId: string;
    stationId: string;
  } | null>(null);
  const save = useSubmit(async () => {
    const body = {
      name: form!.name,
      parentId: form!.parentId || null,
      stationId: form!.stationId || null,
    };
    if (form!.id) await api.patch(`/categories/${form!.id}`, body);
    else await api.post('/categories', body);
    inv('categories');
    setForm(null);
  });
  const remove = useSubmit(async (c: Category) => {
    if (!confirm(`Hapus kategori ${c.name}?`)) return;
    await api.del(`/categories/${c.id}`);
    inv('categories');
  });
  const name = (id: string | null) => data.find((c) => c.id === id)?.name ?? '—';
  const cols: Column<Category>[] = [
    { header: 'Kategori', cell: (c) => <span className="font-medium">{c.name}</span> },
    { header: 'Induk', cell: (c) => name(c.parentId) },
    { header: 'Produk', cell: (c) => c.productCount },
    {
      header: '',
      className: 'text-right',
      cell: (c) =>
        can('catalog:write') && (
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                setForm({
                  id: c.id,
                  name: c.name,
                  parentId: c.parentId ?? '',
                  stationId: c.stationId ?? '',
                })
              }
            >
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void remove.run(c)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Kategori"
        subtitle="Kelompokkan produk agar mudah dicari di kasir"
        actions={
          can('catalog:write') && (
            <Button onClick={() => setForm({ name: '', parentId: '', stationId: '' })}>
              + Tambah kategori
            </Button>
          )
        }
      />
      {remove.error && <Alert>{remove.error}</Alert>}
      <Card>{isLoading ? 'Memuat…' : <DataTable columns={cols} rows={data} />}</Card>
      <Modal
        open={!!form}
        title={form?.id ? 'Ubah kategori' : 'Tambah kategori'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Nama"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoFocus
            />
            <SelectField
              label="Induk (opsional)"
              value={form.parentId}
              onChange={(e) => setForm({ ...form, parentId: e.target.value })}
            >
              <option value="">— Tanpa induk —</option>
              {data
                .filter((c) => c.id !== form.id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </SelectField>
            <StationSelect
              value={form.stationId}
              onChange={(v) => setForm({ ...form, stationId: v })}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ---------- Produk ----------
interface VariantForm {
  id?: string;
  name: string;
  sku: string;
  barcode: string;
  price: string;
  cost: string;
  active: boolean;
}
interface ProductForm {
  id?: string;
  name: string;
  categoryId: string;
  description: string;
  type: ProductDto['type'];
  unit: string;
  taxable: boolean;
  trackStock: boolean;
  active: boolean;
  imageUrl: string | null;
  stationId: string;
  variants: VariantForm[];
  modifierGroupIds: string[];
  bundleItems: { variantId: string; qty: number }[];
}
const emptyVariant = (name = 'Default'): VariantForm => ({
  name,
  sku: '',
  barcode: '',
  price: '',
  cost: '',
  active: true,
});
const emptyProduct = (): ProductForm => ({
  name: '',
  categoryId: '',
  description: '',
  type: 'simple',
  unit: 'pcs',
  taxable: true,
  trackStock: false,
  active: true,
  imageUrl: null,
  stationId: '',
  variants: [emptyVariant()],
  modifierGroupIds: [],
  bundleItems: [],
});
const fromDto = (p: ProductDto): ProductForm => ({
  id: p.id,
  name: p.name,
  categoryId: p.categoryId ?? '',
  description: p.description ?? '',
  type: p.type,
  unit: p.unit,
  taxable: p.taxable,
  trackStock: p.trackStock,
  active: p.active,
  imageUrl: p.imageUrl,
  stationId: p.stationId ?? '',
  modifierGroupIds: p.modifierGroupIds,
  bundleItems: p.bundleItems,
  variants: p.variants.map((v) => ({
    id: v.id,
    name: v.name,
    sku: v.sku ?? '',
    barcode: v.barcode ?? '',
    price: String(v.price),
    cost: String(v.cost),
    active: v.active,
  })),
});
const toPayload = (f: ProductForm) => ({
  name: f.name.trim(),
  categoryId: f.categoryId || null,
  stationId: f.stationId || null,
  description: f.description.trim() || null,
  type: f.type,
  unit: f.unit.trim() || 'pcs',
  taxable: f.taxable,
  trackStock: f.trackStock,
  active: f.active,
  modifierGroupIds: f.modifierGroupIds,
  bundleItems: f.type === 'bundle' ? f.bundleItems : [],
  variants: f.variants.map((v) => ({
    ...(v.id ? { id: v.id } : {}),
    name: v.name.trim() || 'Default',
    sku: v.sku.trim() || null,
    barcode: v.barcode.trim() || null,
    price: Number(v.price || 0),
    cost: Number(v.cost || 0),
    active: v.active,
  })),
});

export function ProductsPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('');
  const products = useQuery({
    queryKey: ['products', q, categoryId, status],
    queryFn: () =>
      api.get<{ data: ProductDto[]; nextCursor: string | null }>(
        `/products?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}${categoryId ? `&categoryId=${categoryId}` : ''}${status ? `&active=${status}` : ''}`,
      ),
  });
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  });
  const groups = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: () => api.get<ModifierGroup[]>('/modifier-groups'),
  });
  const bundleOptions = useQuery({
    queryKey: ['products', 'options'],
    queryFn: () => api.get<{ data: ProductDto[] }>('/products?limit=200&active=true'),
  });
  const [form, setForm] = useState<ProductForm | null>(null);

  const save = useSubmit(async () => {
    const payload = toPayload(form!);
    if (form!.id) await api.patch(`/products/${form!.id}`, payload);
    else await api.post('/products', payload);
    inv('products', 'categories', 'catalog-summary');
    setForm(null);
  });
  const remove = useSubmit(async (p: ProductDto) => {
    if (!confirm(`Hapus produk ${p.name}?`)) return;
    await api.del(`/products/${p.id}`);
    inv('products', 'categories', 'catalog-summary');
  });
  const imageUp = useSubmit(async (file: File) => {
    const r = await upload<{ imageUrl: string }>('tenant', `/products/${form!.id}/image`, file);
    setForm({ ...form!, imageUrl: r.imageUrl });
    inv('products');
  });
  const imageDel = useSubmit(async () => {
    await api.del(`/products/${form!.id}/image`);
    setForm({ ...form!, imageUrl: null });
    inv('products');
  });

  const price = (p: ProductDto) => {
    const ps = p.variants.map((v) => v.price);
    if (ps.length === 0) return '—';
    const [lo, hi] = [Math.min(...ps), Math.max(...ps)];
    return lo === hi ? formatRupiah(lo) : `${formatRupiah(lo)} – ${formatRupiah(hi)}`;
  };
  const cols: Column<ProductDto>[] = [
    {
      header: '',
      className: 'w-12',
      cell: (p) =>
        p.imageUrl ? (
          <img src={fileUrl(p.imageUrl)!} alt="" className="h-10 w-10 rounded object-cover" />
        ) : (
          <div className="h-10 w-10 rounded bg-slate-100" />
        ),
    },
    {
      header: 'Produk',
      cell: (p) => (
        <div>
          <div className="font-medium">{p.name}</div>
          <div className="text-xs text-slate-500">
            {p.variants
              .map((v) => v.sku)
              .filter(Boolean)
              .join(', ') || '—'}
          </div>
        </div>
      ),
    },
    { header: 'Kategori', cell: (p) => p.categoryName ?? '—' },
    {
      header: 'Jenis',
      cell: (p) => (
        <Badge tone={p.type === 'bundle' ? 'info' : 'neutral'}>
          {TYPE_LABEL[p.type]}
          {p.type === 'variant' ? ` · ${p.variants.length}` : ''}
        </Badge>
      ),
    },
    { header: 'Harga', cell: price },
    {
      header: 'Status',
      cell: (p) => (
        <Badge tone={p.active ? 'success' : 'neutral'}>{p.active ? 'Aktif' : 'Nonaktif'}</Badge>
      ),
    },
    {
      header: '',
      className: 'text-right',
      cell: (p) =>
        can('catalog:write') && (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setForm(fromDto(p))}>
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void remove.run(p)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];

  const setVariant = (i: number, patch: Partial<VariantForm>) =>
    form &&
    setForm({ ...form, variants: form.variants.map((v, j) => (j === i ? { ...v, ...patch } : v)) });
  const changeType = (type: ProductDto['type']) => {
    if (!form) return;
    const variants = type === 'variant' ? form.variants : form.variants.slice(0, 1);
    setForm({
      ...form,
      type,
      variants,
      unit: type === 'weighed' && form.unit === 'pcs' ? 'kg' : form.unit,
    });
  };
  const bundleVariants = (bundleOptions.data?.data ?? [])
    .filter((p) => p.type !== 'bundle' && p.id !== form?.id)
    .flatMap((p) =>
      p.variants.map((v) => ({
        id: v.id,
        label: `${p.name}${p.type === 'variant' ? ` — ${v.name}` : ''}`,
      })),
    );

  return (
    <>
      <PageHeader
        title="Produk"
        subtitle="Katalog menu / barang jualan"
        actions={
          can('catalog:write') && (
            <Button onClick={() => setForm(emptyProduct())}>+ Tambah produk</Button>
          )
        }
      />
      {remove.error && <Alert>{remove.error}</Alert>}
      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <TextField
            label="Cari nama / SKU / barcode"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <SelectField
            label="Kategori"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Semua</option>
            {categories.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </SelectField>
          <SelectField label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Semua</option>
            <option value="true">Aktif</option>
            <option value="false">Nonaktif</option>
          </SelectField>
        </div>
        {products.isLoading ? (
          'Memuat…'
        ) : (
          <DataTable
            columns={cols}
            rows={products.data?.data ?? []}
            empty="Belum ada produk. Tambahkan satu, atau impor dari CSV."
          />
        )}
        {products.data?.nextCursor && (
          <p className="mt-3 text-xs text-slate-500">
            Menampilkan 100 produk pertama — gunakan pencarian/filter untuk mempersempit.
          </p>
        )}
      </Card>

      <Modal
        open={!!form}
        title={form?.id ? 'Ubah produk' : 'Tambah produk'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {(save.error || imageUp.error || imageDel.error) && (
              <Alert>{save.error ?? imageUp.error ?? imageDel.error}</Alert>
            )}
            <TextField
              label="Nama produk"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoFocus
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="Kategori"
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              >
                <option value="">— Tanpa kategori —</option>
                {categories.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </SelectField>
              <StationSelect
                label="Stasiun (menimpa kategori)"
                value={form.stationId}
                onChange={(v) => setForm({ ...form, stationId: v })}
              />
              <SelectField
                label="Jenis"
                value={form.type}
                onChange={(e) => changeType(e.target.value as ProductDto['type'])}
              >
                <option value="simple">Sederhana (1 harga)</option>
                <option value="variant">Bervarian (ukuran/warna)</option>
                <option value="weighed">Timbang (kg/gram)</option>
                <option value="bundle">Paket</option>
              </SelectField>
              <TextField
                label="Satuan"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              />
              <TextField
                label="Deskripsi"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.taxable}
                  onChange={(e) => setForm({ ...form, taxable: e.target.checked })}
                />{' '}
                Kena pajak
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.trackStock}
                  onChange={(e) => setForm({ ...form, trackStock: e.target.checked })}
                />{' '}
                Lacak stok
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />{' '}
                Aktif dijual
              </label>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700">
                {form.type === 'variant' ? 'Varian & harga' : 'Harga'}
              </legend>
              {form.variants.map((v, i) => (
                <div
                  key={v.id ?? i}
                  className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-6"
                >
                  {form.type === 'variant' && (
                    <div className="sm:col-span-2">
                      <TextField
                        label="Nama varian"
                        value={v.name}
                        onChange={(e) => setVariant(i, { name: e.target.value })}
                        required
                      />
                    </div>
                  )}
                  <div className={form.type === 'variant' ? 'sm:col-span-2' : 'sm:col-span-3'}>
                    <TextField
                      label="Harga jual (Rp)"
                      inputMode="numeric"
                      value={v.price}
                      onChange={(e) => setVariant(i, { price: e.target.value.replace(/\D/g, '') })}
                      required
                    />
                  </div>
                  <div className={form.type === 'variant' ? 'sm:col-span-2' : 'sm:col-span-3'}>
                    <TextField
                      label="Harga modal (Rp)"
                      inputMode="numeric"
                      value={v.cost}
                      onChange={(e) => setVariant(i, { cost: e.target.value.replace(/\D/g, '') })}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <TextField
                      label="SKU"
                      value={v.sku}
                      onChange={(e) => setVariant(i, { sku: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <TextField
                      label="Barcode"
                      value={v.barcode}
                      onChange={(e) => setVariant(i, { barcode: e.target.value })}
                    />
                  </div>
                  {form.type === 'variant' && form.variants.length > 1 && (
                    <div className="sm:col-span-6 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() =>
                          setForm({ ...form, variants: form.variants.filter((_, j) => j !== i) })
                        }
                      >
                        Hapus varian
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {form.type === 'variant' && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setForm({ ...form, variants: [...form.variants, emptyVariant('')] })
                  }
                >
                  + Tambah varian
                </Button>
              )}
            </fieldset>

            {form.type === 'bundle' && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-700">Isi paket</legend>
                {form.bundleItems.map((b, i) => (
                  <div key={i} className="flex gap-2">
                    <select
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-2 text-sm"
                      value={b.variantId}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          bundleItems: form.bundleItems.map((x, j) =>
                            j === i ? { ...x, variantId: e.target.value } : x,
                          ),
                        })
                      }
                    >
                      <option value="">Pilih produk…</option>
                      {bundleVariants.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label="Jumlah"
                      type="number"
                      min={1}
                      className="w-20 rounded-lg border border-slate-300 px-2 py-2 text-sm"
                      value={b.qty}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          bundleItems: form.bundleItems.map((x, j) =>
                            j === i ? { ...x, qty: Math.max(1, Number(e.target.value)) } : x,
                          ),
                        })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() =>
                        setForm({
                          ...form,
                          bundleItems: form.bundleItems.filter((_, j) => j !== i),
                        })
                      }
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setForm({
                      ...form,
                      bundleItems: [...form.bundleItems, { variantId: '', qty: 1 }],
                    })
                  }
                >
                  + Tambah item
                </Button>
              </fieldset>
            )}

            {(groups.data?.length ?? 0) > 0 && (
              <fieldset>
                <legend className="mb-1 text-sm font-medium text-slate-700">
                  Modifier (pilihan tambahan)
                </legend>
                <div className="space-y-1">
                  {groups.data!.map((g) => (
                    <label key={g.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.modifierGroupIds.includes(g.id)}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            modifierGroupIds: e.target.checked
                              ? [...form.modifierGroupIds, g.id]
                              : form.modifierGroupIds.filter((x) => x !== g.id),
                          })
                        }
                      />
                      {g.name}{' '}
                      <span className="text-xs text-slate-500">
                        ({g.modifiers.length} pilihan{g.required ? ', wajib' : ''})
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            <fieldset>
              <legend className="mb-1 text-sm font-medium text-slate-700">Gambar</legend>
              {form.id ? (
                <div className="flex items-center gap-3">
                  {form.imageUrl ? (
                    <img
                      src={fileUrl(form.imageUrl)!}
                      alt="Gambar produk"
                      className="h-16 w-16 rounded object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded bg-slate-100" />
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    aria-label="Unggah gambar"
                    onChange={(e) => e.target.files?.[0] && void imageUp.run(e.target.files[0])}
                    className="text-sm"
                  />
                  {form.imageUrl && (
                    <Button type="button" variant="ghost" onClick={() => void imageDel.run()}>
                      Hapus
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Simpan produk terlebih dahulu, lalu unggah gambar (JPG/PNG/WEBP, maks. 2 MB).
                </p>
              )}
            </fieldset>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ---------- Modifier ----------
interface GroupForm {
  id?: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  modifiers: { id?: string; name: string; priceDelta: string; active: boolean }[];
}

export function ModifiersPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const { data = [], isLoading } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: () => api.get<ModifierGroup[]>('/modifier-groups'),
  });
  const [form, setForm] = useState<GroupForm | null>(null);
  const save = useSubmit(async () => {
    const body = {
      name: form!.name.trim(),
      minSelect: form!.minSelect,
      maxSelect: form!.maxSelect,
      modifiers: form!.modifiers.map((m) => ({
        ...(m.id ? { id: m.id } : {}),
        name: m.name.trim(),
        priceDelta: Number(m.priceDelta || 0),
        active: m.active,
      })),
    };
    if (form!.id) await api.put(`/modifier-groups/${form!.id}`, body);
    else await api.post('/modifier-groups', body);
    inv('modifier-groups', 'products');
    setForm(null);
  });
  const remove = useSubmit(async (g: ModifierGroup) => {
    if (!confirm(`Hapus grup ${g.name}? Grup akan dilepas dari ${g.productCount} produk.`)) return;
    await api.del(`/modifier-groups/${g.id}`);
    inv('modifier-groups', 'products');
  });
  const edit = (g: ModifierGroup) =>
    setForm({
      id: g.id,
      name: g.name,
      minSelect: g.minSelect,
      maxSelect: g.maxSelect,
      modifiers: g.modifiers.map((m) => ({
        id: m.id,
        name: m.name,
        priceDelta: String(m.priceDelta),
        active: m.active,
      })),
    });
  const cols: Column<ModifierGroup>[] = [
    { header: 'Grup', cell: (g) => <span className="font-medium">{g.name}</span> },
    {
      header: 'Aturan',
      cell: (g) =>
        `${g.required ? 'Wajib' : 'Opsional'} · pilih ${g.minSelect === g.maxSelect ? g.maxSelect : `${g.minSelect}–${g.maxSelect}`}`,
    },
    {
      header: 'Pilihan',
      cell: (g) =>
        g.modifiers.map((m) => m.name + (m.priceDelta ? ` (+${m.priceDelta})` : '')).join(', '),
    },
    { header: 'Produk', cell: (g) => g.productCount },
    {
      header: '',
      className: 'text-right',
      cell: (g) =>
        can('catalog:write') && (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => edit(g)}>
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void remove.run(g)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];
  const upd = (i: number, patch: Partial<GroupForm['modifiers'][number]>) =>
    form &&
    setForm({
      ...form,
      modifiers: form.modifiers.map((m, j) => (j === i ? { ...m, ...patch } : m)),
    });
  return (
    <>
      <PageHeader
        title="Modifier"
        subtitle="Pilihan tambahan produk, mis. level gula, topping"
        actions={
          can('catalog:write') && (
            <Button
              onClick={() =>
                setForm({
                  name: '',
                  minSelect: 0,
                  maxSelect: 1,
                  modifiers: [{ name: '', priceDelta: '0', active: true }],
                })
              }
            >
              + Grup modifier
            </Button>
          )
        }
      />
      {remove.error && <Alert>{remove.error}</Alert>}
      <Card>
        {isLoading ? (
          'Memuat…'
        ) : (
          <DataTable columns={cols} rows={data} empty="Belum ada grup modifier." />
        )}
      </Card>
      <Modal
        open={!!form}
        title={form?.id ? 'Ubah grup modifier' : 'Grup modifier baru'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Nama grup"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoFocus
            />
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Minimal dipilih"
                type="number"
                min={0}
                value={form.minSelect}
                onChange={(e) => setForm({ ...form, minSelect: Number(e.target.value) })}
                hint="0 = opsional, 1+ = wajib"
              />
              <TextField
                label="Maksimal dipilih"
                type="number"
                min={1}
                value={form.maxSelect}
                onChange={(e) => setForm({ ...form, maxSelect: Number(e.target.value) })}
              />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700">Pilihan</legend>
              {form.modifiers.map((m, i) => (
                <div key={m.id ?? i} className="flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <TextField
                      label="Nama"
                      value={m.name}
                      onChange={(e) => upd(i, { name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="w-32">
                    <TextField
                      label="Tambah harga"
                      inputMode="numeric"
                      value={m.priceDelta}
                      onChange={(e) =>
                        upd(i, { priceDelta: e.target.value.replace(/[^\d-]/g, '') })
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={form.modifiers.length === 1}
                    onClick={() =>
                      setForm({ ...form, modifiers: form.modifiers.filter((_, j) => j !== i) })
                    }
                  >
                    ✕
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setForm({
                    ...form,
                    modifiers: [...form.modifiers, { name: '', priceDelta: '0', active: true }],
                  })
                }
              >
                + Tambah pilihan
              </Button>
            </fieldset>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
